// src/pages/DashboardPage.js

import React, { useState, useEffect } from "react";
import { DataGrid } from "@mui/x-data-grid";
import { AiFillEye } from "react-icons/ai";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getAllClients, getAllTasks } from "../../../redux/actions/authActions";

const DashboardPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const clients = useSelector((state) => state.auth.allClients);
  const tasks = useSelector((state) => state.auth.allTasks);

  const [searchValue, setSearchValue] = useState("");
  const [rows, setRows] = useState([]);
  const [allRows, setAllRows] = useState([]); // Backup list for full restore

  const [totalClients, setTotalClients] = useState(0);
  const [tasksInProgress, setTasksInProgress] = useState(0);
  const [tasksCompleted, setTasksCompleted] = useState(0);

  const token = localStorage.getItem("token");

  useEffect(() => {
    try {
      if (token) {
        dispatch(getAllClients(token));
        dispatch(getAllTasks(token));
      }
    }
    catch (err) {
      console.log(err);
    }
  }, [dispatch, token]);

  useEffect(() => {
    if (Array.isArray(clients)) {
      const formattedRows = clients.map((client, index) => {
        // Since the backend populates the 'client' field in the task, task.client is an object.
        // We compare using its ID to correctly filter clientTasks.
        const clientTasks = Array.isArray(tasks)
          ? tasks.filter((task) => {
              const taskClientId = task.client?._id || task.client;
              return taskClientId === client._id;
            })
          : [];

        const dueDates = clientTasks.map((task) => new Date(task.dueDate));
        const dueDate =
          dueDates.length > 0
            ? new Date(Math.min(...dueDates)).toLocaleDateString("en-GB")
            : "N/A";

        // Map assignees by ID/object uniquely
        const uniqueAssignees = Array.from(
          new Set(clientTasks.flatMap((task) => (task.assignees || []).map((a) => a._id || a)))
        );
        const assigneeDisplay =
          uniqueAssignees.length === 0
            ? "N/A"
            : uniqueAssignees.length === 1
              ? "1 assignee"
              : `${uniqueAssignees.length} assignees`;

        const totalProgress = clientTasks.reduce((sum, task) => {
          if (task.status === "Completed") return sum + 100;
          return sum + (task.progress || 0);
        }, 0);
        const progress = clientTasks.length
          ? Math.round(totalProgress / clientTasks.length)
          : 0;

        const status =
          progress === 100
            ? "Completed"
            : progress > 0
              ? "In Progress"
              : "Upcoming";

        return {
          id: client._id,
          clientId: client.id || `CLT-${index + 1}`,
          clientName: client.businessName || client.name,
          email: client.email,
          dueDate,
          assignee: assigneeDisplay,
          progress,
          status,
        };
      });

      setRows(formattedRows);
      setAllRows(formattedRows); // Store backup for search reset
      setTotalClients(clients.length);

      // Support both "In Progress" and backend standard "InProgress" statuses
      const inProgressCount = Array.isArray(tasks)
        ? tasks.filter((task) =>
            ["In Progress", "InProgress", "Pending"].includes(task.status)
          ).length
        : 0;
      const completedCount = Array.isArray(tasks)
        ? tasks.filter((task) => task.status === "Completed").length
        : 0;

      setTasksInProgress(inProgressCount);
      setTasksCompleted(completedCount);
    }
  }, [clients, tasks]);

  const handleSearch = (query) => {
    setSearchValue(query);

    if (!query || query.trim() === "") {
      setRows(allRows); // Reset view on backspace/empty
      return;
    }

    const filtered = allRows.filter(
      (row) =>
        row.clientName?.toLowerCase().includes(query.toLowerCase()) ||
        row.email?.toLowerCase().includes(query.toLowerCase()) ||
        row.clientId?.toLowerCase().includes(query.toLowerCase())
    );
    setRows(filtered);
  };

  const getProgressColor = (progress) => {
    if (progress === 100) return "bg-green-500";
    if (progress >= 50) return "bg-yellow-400";
    if (progress > 0) return "bg-orange-400";
    return "bg-gray-400";
  };

  const handleView = (id) => {
    navigate(`view-client/${id}`);
  };

  const columns = [
    { field: "clientId", headerName: "Client ID", width: 120 },
    { field: "clientName", headerName: "Client Name", width: 150 },
    { field: "email", headerName: "Email Address", width: 200 },
    { field: "dueDate", headerName: "Due Date", width: 120 },
    {
      field: "assignee",
      headerName: "Assignee",
      width: 150,
    },
    {
      field: "progress",
      headerName: "Progress",
      width: 150,
      renderCell: (params) => (
        <div className="flex items-center mt-5 justify-center w-full space-x-4">
          <div className="w-24 bg-gray-300 rounded-full h-2.5 dark:bg-gray-700">
            <div
              className={`h-2.5 rounded-full ${getProgressColor(params.value)}`}
              style={{ width: `${params.value}%` }}
            ></div>
          </div>
          <span className="text-xs font-medium text-gray-700">{params.value}%</span>
        </div>
      ),
    },
    {
      field: "status",
      headerName: "Status",
      width: 120,
      renderCell: (params) => (
        <span
          className={`px-2 py-1 rounded-full text-xs font-semibold
            ${params.value === "Completed" ? "bg-green-100 text-green-800" : ""}
            ${params.value === "In Progress" ? "bg-orange-100 text-orange-800" : ""}
            ${params.value === "Upcoming" ? "bg-blue-100 text-blue-800" : ""}
          `}
        >
          {params.value}
        </span>
      ),
    },
    {
      field: "view",
      headerName: "View",
      width: 80,
      renderCell: (params) => (
        <button
          className="bg-gray-100 p-2 rounded-lg text-blue-700 border shadow"
          onClick={() => handleView(params?.row?.id)}
        >
          <AiFillEye />
        </button>
      ),
    },
  ];

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white p-4 rounded-lg shadow-md flex items-center border border-gray-300">
          <div className="bg-[#045B9B] text-white rounded-lg p-3 mr-4">
            <div className="text-2xl font-bold">{totalClients}</div>
          </div>
          <div className="text-gray-600 font-bold">Total Clients Assigned</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-md flex items-center border border-gray-300">
          <div className="bg-[#FF8036] text-white rounded-lg p-3 mr-4">
            <div className="text-2xl font-bold">{tasksInProgress}</div>
          </div>
          <div className="text-gray-600">Tasks In Progress</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-md flex items-center border border-gray-300">
          <div className="bg-[#12B769] text-white rounded-lg p-3 mr-4">
            <div className="text-2xl font-bold">{tasksCompleted}</div>
          </div>
          <div className="text-gray-600">Tasks Completed</div>
        </div>
      </div>

      <h2 className="text-2xl font-semibold text-gray-800 mb-6">All Clients & Tasks</h2>

      <div className="mb-4">
        <input
          type="text"
          placeholder="Search by name, email, ID"
          className="border p-2 rounded w-full sm:w-1/3"
          value={searchValue}
          onChange={(e) => handleSearch(e.target.value)}
        />
      </div>

      <div className="w-full bg-white rounded-lg shadow overflow-hidden">
        <div className="h-[calc(100vh-280px)] min-h-[400px]">
          <DataGrid
            rows={rows}
            columns={columns}
            getRowId={(row) => row.id}
            pageSizeOptions={[5, 8, 10, 25]}
            initialState={{ pagination: { paginationModel: { pageSize: 8, page: 0 } } }}
            checkboxSelection
            pagination
          />
        </div>
      </div>
    </>
  );
};

export default DashboardPage;
